import React, { useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../../../overmind';
import { Pager, SearchBar, Button, DropdownSelectOption, ListItemLayout, ListLayout } from '@keplerco/core';
import classNames from 'classnames';
import { FilteringSearchParams, PagingSearchParams, SearchingSearchParams, SortingSearchParamsDeprecated, UsersListSearchParams } from '../../../../../models/overmind/search-params';
import { EmptyState } from '../../../../../components/general/empty-state/empty-state';
import { AssessmentProgressLayoutProps } from './assessment-progress.models';
import { CompletionStatus } from '../../../../../enums';
import { AnalyticsFilters } from '../../../analytics-filters/analytics-filters';
import { SkeletonOverlay } from '../../../../../components/general/loading-state/skeleton-overlay';
import { SortBy } from '../../../../../components/general/sort-by/sort-by';
import { SortByOption, SortByOptionDescription } from '../../../../../enums/sort-by-option';
import { WidgetCard } from '../../../analytics-filters/widget-card';
import { DownloadIcon } from '../../../../../design/icons/download.icon';
import { FiltersOutlet } from '../../../analytics-filters/filters-outlet';
import { SelectedFilterOption } from '../../../analytics-filters/analytics-filters.models';
import { getSelectedFilters, removeFilterOption } from '../../../analytics-filters/analytics-filters.helpers';
import { DEFAULT_FILTERING_SEARCH_PARAMS } from '../../../../../library/consts/consts';
import { AssessmentSubType, AssessmentType } from '../../../../../enums/assessment-type';
import { AssessmentProgressAccordion } from './assessment-progress-accordion';
import { AssessmentAssignee } from '../../../../../models/overmind/assessment-analytics';
import { CompletionStatusChip } from '../../../../../components/chips/completion-status.chip';
import { PagePath } from '../../../../../navigation/navigation.enums';
import { useKeplerNavigate } from '../../../../../navigation/guards/use-kepler-navigate';
import styles from './assessment-progress.module.css';
import { getStatusColour } from './assessment-progress-helpers';
import { AssessmentAssigneeListItem } from '../../../../../overmind/actions';

const SORT_BY_OPTIONS: DropdownSelectOption[] = [
  {
    value: SortByOption.AlphabeticalAtoZ,
    label: SortByOptionDescription.AlphabeticalAtoZ,
  },
  {
    value: SortByOption.AlphabeticalZtoA,
    label: SortByOptionDescription.AlphabeticalZtoA,
  },
  {
    value: SortByOption.RecentlyCompleted,
    label: SortByOptionDescription.RecentlyCompleted,
  },
];

const STATUS_OPTIONS: DropdownSelectOption[] = [
  { value: String(CompletionStatus.NotStarted), label: 'Not Started' },
  { value: String(CompletionStatus.InProgress), label: 'In Progress' },
  { value: String(CompletionStatus.Completed), label: 'Completed' },
];

type SearchParams = SearchingSearchParams & SortingSearchParamsDeprecated & PagingSearchParams;

function getSortByValue(sortBy: string | undefined, sortAscending: boolean | undefined): string | undefined {
  if (sortBy === '0' && sortAscending) return SortByOption.AlphabeticalAtoZ;
  if (sortBy === '0' && !sortAscending) return SortByOption.AlphabeticalZtoA;
  if (sortBy === '1') return SortByOption.RecentlyCompleted;
  return undefined;
}

export function AssessmentProgressLayout({ baseSearchParams, departmentOptions, teamOptions, roleOptions, countryOptions }: AssessmentProgressLayoutProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterOption[]>([]);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    search: undefined,
    sortBy: undefined,
    sortAscending: true,
    page: 1,
    pageSize: 15,
  });
  const [filteringSearchParams, setFilteringSearchParams] = useState<FilteringSearchParams>(DEFAULT_FILTERING_SEARCH_PARAMS);
  const [peerAssessmentSubjects, setPeerAssessmentSubjects] = useState<AssessmentAssignee[]>();
  const [otherAssessmentSubjects, setOtherAssessmentSubjects] = useState<AssessmentAssigneeListItem[]>();
  const [totalPages, setTotalPages] = useState<number>(0);


  async function getData(params: UsersListSearchParams) {
    if (baseSearchParams.assessmentType === AssessmentType.PeerEndorsement) {
      const response = await actions.getAssessmentAnalyticsUsers(params);
      setPeerAssessmentSubjects(response?.assignees);
      setTotalPages(response?.totalPages ?? 0);
    } else {
      const response = await actions.getAssessmentAssigneeList({ assessmentSlug: params.assessmentSlug, searchParams: params });
      setOtherAssessmentSubjects(response?.list);
      setTotalPages(response?.totalPages ?? 0);
    }
  }

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
      setIsLoading(false);
    }

    initialise();
  }, [baseSearchParams]);

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%">
      <WidgetCard title={baseSearchParams.assessmentSubType === AssessmentSubType.RatingAssessment ? 'Assessment subjects' : 'Assessment participants'} style={{ width: '100%' }}>
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
            <SearchBar
              label="Search"
              loading={isSearching}
              onInput={async event => {
                const search = (event.target as HTMLInputElement).value;
                setIsSearching(true);
                setSearchParams({ ...baseSearchParams, ...searchParams, search, page: 1 });
                await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, search, page: 1 });
                setIsSearching(false);
              }}
            />

            <AnalyticsFilters
              departmentOptions={departmentOptions}
              teamOptions={teamOptions}
              roleOptions={roleOptions}
              countryOptions={countryOptions}
              statusOptions={STATUS_OPTIONS}
              filteringSearchParams={filteringSearchParams}
              setFilteringSearchParams={setFilteringSearchParams}
              applyFilters={async () => {
                setIsLoading(true);
                const selectedFilters = getSelectedFilters({ searchParams: filteringSearchParams, departmentOptions, teamOptions, roleOptions, countryOptions, statusOptions: STATUS_OPTIONS });
                setSelectedFilters(selectedFilters);
                await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
                setIsLoading(false);
              }}
              clearFilters={async () => {
                if (!filteringSearchParams.departmentSlugs && !filteringSearchParams.teamSlugs && !filteringSearchParams.roleSlugs && !filteringSearchParams.countryIds && !filteringSearchParams.completionStatus) return;
                setIsLoading(true);
                setSearchParams(searchParams);
                setFilteringSearchParams(DEFAULT_FILTERING_SEARCH_PARAMS);
                setSelectedFilters([]);
                await getData({ ...baseSearchParams, ...searchParams, ...DEFAULT_FILTERING_SEARCH_PARAMS });
                setIsLoading(false);
              }}
            />

            <SortBy
              options={SORT_BY_OPTIONS}
              value={getSortByValue(searchParams.sortBy, searchParams.sortAscending)}
              onChange={async event => {
                setIsLoading(true);
                const value = event.target.value;
                let sortBy;
                let sortAscending;
                if (value === SortByOption.AlphabeticalAtoZ) {
                  sortBy = '0';
                  sortAscending = true;
                } else if (value === SortByOption.AlphabeticalZtoA) {
                  sortBy = '0';
                  sortAscending = false;
                } else if (value === SortByOption.RecentlyCompleted) {
                  sortBy = '1';
                  sortAscending = false;
                }
                setSearchParams({ ...searchParams, sortBy, sortAscending });
                await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, sortBy, sortAscending });
                setIsLoading(false);
              }}
            />
            <div style={{ marginLeft: 'auto' }}>
              <Button
                square
                theme="dark"
                type="button"
                isLoading={isDownloading}
                loaderText="Downloading..."
                onClick={async () => {
                  setIsDownloading(true);
                  await actions.getAssessmentAssigneeListCSV(baseSearchParams.assessmentSlug);
                  setIsDownloading(false);
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <DownloadIcon />
                  CSV
                </div>
              </Button>
            </div>
          </div>

          <FiltersOutlet
            selectedFilters={selectedFilters}
            removeFilter={async filter => {
              setIsLoading(true);
              const updatedFilteringParams = removeFilterOption(filteringSearchParams, filter);
              setFilteringSearchParams(updatedFilteringParams);
              setSelectedFilters(selectedFilters.filter(selectedFilter => selectedFilter.slug !== filter.slug));
              await getData({ ...baseSearchParams, ...searchParams, ...updatedFilteringParams });
              setIsLoading(false);
            }}
          />
        </div>

        {baseSearchParams.assessmentType === AssessmentType.PeerEndorsement && !!peerAssessmentSubjects?.length ? (
          peerAssessmentSubjects?.map(subject => <AssessmentProgressAccordion key={subject.learnerSlug} assignee={subject} />)
        ) : baseSearchParams.assessmentType !== AssessmentType.PeerEndorsement && !!otherAssessmentSubjects?.length ? (
          <ListLayout>
            {otherAssessmentSubjects.map(participant => {
              return (
                <ListItemLayout
                  key={participant.learnerSlug}
                  onClick={() => keplerNavigate(`${PagePath.analyticsBase}${PagePath.analyticsAssessmentPerson.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', baseSearchParams.assessmentSlug).replace(':personSlug', participant.learnerSlug)}`)}
                >
                  <div className={classNames('card', styles.card)} style={{ borderLeftColor: getStatusColour(participant.completionStatus) }}>
                    <div>
                      <div>
                        {participant.firstName} {participant.lastName}
                      </div>

                      <small style={{ color: 'var(--text_1)' }}>
                        {participant.departmentName || 'No department'} | {participant.teamName || 'No team'}
                      </small>
                    </div>

                    <div>{participant.role}</div>

                    <div>{participant.email}</div>

                    <div>
                      <CompletionStatusChip completionStatus={participant.completionStatus} />
                    </div>
                  </div>
                </ListItemLayout>
              );
            })}
          </ListLayout>
        ) : (
          <EmptyState title="Hang tight!" subtitle="We are waiting for data." />
        )}

        <div className={classNames({ invisible: !totalPages || totalPages < 2 })}>
          <Pager
            activePageNumber={searchParams.page ?? 1}
            pageCount={totalPages}
            onPageChange={async page => {
              if (page === searchParams.page) return;
              setIsLoading(true);
              setSearchParams({ ...searchParams, page });
              await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, page });
              setIsLoading(false);
            }}
          />
        </div>
      </WidgetCard>
    </SkeletonOverlay>
  );
}
